import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../components/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { apiError } from '../../lib/api';
import Turnstile, { TURNSTILE_SITE_KEY } from '../../components/Turnstile';

function initialRefCode() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('ref');
    return (fromUrl || localStorage.getItem('chathub_ref') || '').trim().toUpperCase();
  } catch {
    return '';
  }
}

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', organization: '', password: '', referral_code: initialRefCode() });
  const [loading, setLoading] = useState(false);
  const captchaRef = React.useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Fetch a guaranteed-fresh, single-use captcha token at submit time so a
      // slow form fill or a retry can never send a stale/consumed one.
      let token;
      if (TURNSTILE_SITE_KEY) {
        token = await captchaRef.current?.getToken();
        if (!token) {
          toast.error(captchaRef.current?.scriptBlocked()
            ? 'Le contrôle de sécurité est bloqué : désactive ton bloqueur de publicités sur ce site, puis réessaie.'
            : 'Le contrôle anti-robot a échoué, réessaie.');
          setLoading(false); return;
        }
      }
      await register({ ...form, turnstile_token: token || undefined });
      try { localStorage.removeItem('chathub_ref'); } catch { /* ignore */ }
      toast.success('Compte créé ! Des données de démonstration ont été chargées.');
      nav('/dashboard');
    } catch (err) {
      // Token already consumed by the attempt — refresh for the next try.
      captchaRef.current?.reset();
      toast.error(apiError(err, 'La création du compte a échoué'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#06060a] grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-white/10 bg-[#0B0B0E]/80 backdrop-blur-xl p-8">
          <div className="flex flex-col items-center mb-6"><Logo size="lg" /></div>
          <h1 className="text-3xl font-display font-bold text-white text-center mb-2">Créer le compte</h1>
          <p className="text-sm text-slate-400 text-center mb-7">Crée le compte de ton agence. Aucune période d’essai : l’abonnement démarre au paiement, et tu peux résilier quand tu veux.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Prénom</Label>
                <Input data-testid="register-firstname-input" value={form.first_name} onChange={set('first_name')} className="bg-white/5 border-white/10 text-white h-11" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Nom</Label>
                <Input data-testid="register-lastname-input" value={form.last_name} onChange={set('last_name')} className="bg-white/5 border-white/10 text-white h-11" required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">E-mail</Label>
              <Input data-testid="register-email-input" type="email" value={form.email} onChange={set('email')} className="bg-white/5 border-white/10 text-white h-11" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Nom de l’agence</Label>
              <Input data-testid="register-organization-input" value={form.organization} onChange={set('organization')} className="bg-white/5 border-white/10 text-white h-11" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Mot de passe</Label>
              <Input data-testid="register-password-input" type="password" value={form.password} onChange={set('password')} className="bg-white/5 border-white/10 text-white h-11" minLength={8} required />
              <p className="text-[11px] text-slate-500">8 caractères minimum.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Code de parrainage <span className="text-slate-500">(facultatif)</span></Label>
              <Input data-testid="register-referral-input" value={form.referral_code} onChange={set('referral_code')} placeholder="ex. CH-XXXXXX" className="bg-white/5 border-white/10 text-white h-11 font-mono uppercase" />
            </div>
            <Turnstile ref={captchaRef} />
            <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-11">
              {loading ? 'Création...' : 'Créer le compte'}
            </Button>
            <div className="text-center text-sm text-slate-400 pt-2">
              Déjà un compte ? <Link data-testid="register-signin-link" to="/auth/login" className="text-indigo-400 hover:text-indigo-300">Se connecter</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
